import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuthRedirect } from './rtk';

import {
  SegmentationPredictionComparisonResponse,
  FrameWithLabelAndPredictionParams,
} from 'models/predictions';

export const FrameWithLabelAndPredictionapi = createApi({
  reducerPath: 'predictionFrameWithLabelAndPredictionsAPI',
  baseQuery: baseQueryWithAuthRedirect,
  tagTypes: ['FrameWithLabelAndPrediction'],
  endpoints: builder => ({
    getPredictions: builder.query<
      SegmentationPredictionComparisonResponse,
      FrameWithLabelAndPredictionParams
    >({
      query: (params = {}) => {
        const urlParams = new URLSearchParams();

        // Add basic pagination parameters
        urlParams.append('page', String(params.page || 1));
        urlParams.append('size', String(params.size || 6));

        // Add prediction-specific filters
        if (params.is_reviewed !== undefined) {
          urlParams.append('is_reviewed', String(params.is_reviewed));
        }

        if (params.review_decision) {
          urlParams.append('review_decision', params.review_decision);
        }

        if (params.has_corresponding_label !== undefined) {
          urlParams.append('has_corresponding_label', String(params.has_corresponding_label));
        }

        // Add features as individual segmentation_class parameters
        if (params.features && params.features.length > 0) {
          params.features.forEach(feature => {
            urlParams.append('segmentation_class', feature.uuid);
          });
        }

        // Add datasource parameter
        if (params.datasource) {
          urlParams.append('datasource', params.datasource.uuid);
        }

        // Add DICOM ICID parameter (comma-separated values supported)
        if (params.dicom_icid) {
          urlParams.append('dicom', params.dicom_icid);
        }
        if ((params as any).segmentation_prediction_icid) {
          urlParams.append(
            'segmentation_prediction_icid',
            (params as any).segmentation_prediction_icid
          );
        }
        if ((params as any).segmentation_label_icid) {
          urlParams.append('segmentation_label_icid', (params as any).segmentation_label_icid);
        }

        // Add Study ICID and Study FID parameters separately if provided.
        if (params.study_icid) {
          urlParams.append('study', params.study_icid);
        }
        if ((params as any).study_fid) {
          // send as explicit study_fid so backend can use the dedicated filter
          urlParams.append('study_fid', (params as any).study_fid);
        }

        // Add date range parameters
        if (params.created_at_after) {
          urlParams.append('created_at_after', params.created_at_after);
        }

        if (params.created_at_before) {
          urlParams.append('created_at_before', params.created_at_before);
        }

        if (params.label_created_at_after) {
          urlParams.append('label_created_at_after', params.label_created_at_after);
        }

        if (params.label_created_at_before) {
          urlParams.append('label_created_at_before', params.label_created_at_before);
        }

        // Add source UUID filter when provided (frame/source-level filtering)
        if ((params as any).source_uuid) {
          urlParams.append('source_uuid', (params as any).source_uuid);
        }

        // Add blinded parameter for shuffling segmentation positions
        if (params.blinded !== undefined) {
          urlParams.append('blinded', String(params.blinded));
        }

        // Use the dedicated blinded endpoint when requesting a blinded list.
        const endpointBase = params.blinded ? 'ai-predictions/blinded/' : 'ai-predictions/';

        return {
          url: `${endpointBase}?${urlParams.toString()}`,
        };
      },
      providesTags: (result, error, arg) => [
        {
          type: 'FrameWithLabelAndPrediction',
          id: arg?.blinded ? 'BLINDED_LIST' : 'STANDARD_LIST',
        },
      ],
    }),
  }),
});

export const { useGetPredictionsQuery: FrameWithLabelAndPredictionQuery } =
  FrameWithLabelAndPredictionapi;
